import { useEffect, useMemo, useRef, useState } from 'react';
import { collectionsApi, layoutApi, type SagaSummary, type WidgetOptions, type WidgetSource } from '../api';
import { useApp } from '../context';
import { addWidgetToHome } from '../layout';
import { linkProps, navigate } from '../router';
import { PageHub } from '../components/PageHub';

/**
 * Collecties: filmreeksen (TMDB) gekoppeld aan je bibliotheek, en lijsten uit al je bronnen
 * (SIMKL, Trakt, MDBList, Plex-collecties) die je met één klik als widget op Home zet.
 */

type Filter = 'all' | 'sagas' | 'lists' | 'anime' | 'plex';

interface ListDef { key: string; src: string; srcTone: string; name: string; source: WidgetSource; anime?: boolean; plex?: boolean }

export function Collections() {
  const { plex, libraries } = useApp();
  const [filter, setFilter] = useState<Filter>('all');
  const [sagas, setSagas] = useState<SagaSummary[] | null>(null);
  const [building, setBuilding] = useState<{ done: number; total: number } | null>(null);
  const [sagaError, setSagaError] = useState<string | null>(null);
  const [options, setOptions] = useState<WidgetOptions | null>(null);
  const [plexLists, setPlexLists] = useState<ListDef[]>([]);

  // Reeksen; zolang de koppeling nog wordt opgebouwd elke paar seconden opnieuw
  useEffect(() => {
    let live = true;
    let t: number | undefined;
    const load = () =>
      collectionsApi.list()
        .then((r) => {
          if (!live) return;
          setSagas(r.sagas);
          setBuilding(r.building);
          if (r.building) t = window.setTimeout(load, 4000);
        })
        .catch((e: unknown) => live && setSagaError(e instanceof Error && e.message ? e.message : 'Reeksen laden mislukt.'));
    void load();
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    layoutApi.options().then(setOptions).catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    const video = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
    Promise.all(video.map((l) => plex.collections(l.key).then((cs) => cs.map((c) => ({ c, lib: l.title }))).catch(() => [])))
      .then((all) => setPlexLists(all.flat().slice(0, 24).map(({ c, lib }) => ({
        key: `plex:${c.ratingKey}`, src: `PLEX · ${lib.toUpperCase()}`, srcTone: 'plex', name: c.title, plex: true,
        source: { kind: 'plex', list: 'collection', collection: c.ratingKey, name: c.title } as WidgetSource,
      }))))
      .catch(() => setPlexLists([]));
  }, [plex, libraries]);

  const lists = useMemo<ListDef[]>(() => {
    const out: ListDef[] = [];
    const a = options?.available;
    if (a?.simkl) {
      out.push(
        { key: 'simkl-anime', src: 'SIMKL · ANIME', srcTone: 'simkl', name: 'Trending anime deze week', anime: true, source: { kind: 'simkl', list: 'trending', media: 'anime', period: 'week' } },
        { key: 'simkl-tv', src: 'SIMKL', srcTone: 'simkl', name: 'Trending series deze week', source: { kind: 'simkl', list: 'trending', media: 'tv', period: 'week' } },
        { key: 'simkl-movies', src: 'SIMKL', srcTone: 'simkl', name: 'Trending films deze maand', source: { kind: 'simkl', list: 'trending', media: 'movies', period: 'month' } },
      );
      if (a.simklLinked) out.push({ key: 'simkl-ptw', src: 'SIMKL · JIJ', srcTone: 'simkl', name: 'Mijn Plan to watch', source: { kind: 'simkl', list: 'plantowatch', media: 'anime' } });
    }
    if (a?.trakt) {
      out.push(
        { key: 'trakt-trending', src: 'TRAKT', srcTone: 'trakt', name: 'Trending films', source: { kind: 'trakt', list: 'trending', media: 'movie' } },
        { key: 'trakt-popular-tv', src: 'TRAKT', srcTone: 'trakt', name: 'Populaire series', source: { kind: 'trakt', list: 'popular', media: 'tv' } },
        { key: 'trakt-anticipated', src: 'TRAKT', srcTone: 'trakt', name: 'Meest verwacht', source: { kind: 'trakt', list: 'anticipated', media: 'movie' } },
      );
    }
    for (const l of options?.mdblistTop.slice(0, 10) ?? []) {
      out.push({ key: `mdb:${l.id}`, src: 'MDBLIST', srcTone: 'mdblist', name: l.name, source: { kind: 'mdblist', list: l.id, name: l.name } });
    }
    return out;
  }, [options]);

  const almost = (sagas ?? []).filter((s) => s.have > 0 && s.total - s.have >= 1 && s.total - s.have <= 2).sort((a, b) => (a.total - a.have) - (b.total - b.have) || b.have - a.have);
  const showSagas = filter === 'all' || filter === 'sagas';
  const shownLists = filter === 'anime' ? lists.filter((l) => l.anime) : filter === 'plex' ? plexLists : filter === 'lists' || filter === 'all' ? [...lists, ...plexLists] : [];

  return (
    <main className="page collections-page">
      <PageHub page="collections" />
      <header className="page-head">
        <h1>Collecties</h1>
      </header>
      <div className="chip-wrap coll-filter" role="group" aria-label="Filter" data-focus-group>
        {([['all', 'Alles'], ['sagas', 'Filmreeksen'], ['lists', 'Lijsten'], ['anime', 'Anime'], ['plex', 'Plex-collecties']] as const).map(([id, label]) => (
          <button key={id} className="chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>{label}</button>
        ))}
      </div>

      {showSagas && (
        <>
          {sagaError && <p className="placeholder-row">{sagaError}</p>}
          {building && (
            <p className="placeholder-row">Filmreeksen zoeken in je bibliotheek… {building.done} van {building.total} films bekeken. Dit gebeurt één keer; daarna is het direct.</p>
          )}
          {almost.length > 0 && (
            <section className="row" aria-label="Bijna compleet">
              <h2>Bijna compleet <span className="row-sub">Reeksen waar je nog maar één of twee delen van mist</span></h2>
              <div className="row-track" data-focus-group>
                {almost.map((s) => <AlmostCard key={s.id} s={s} />)}
              </div>
            </section>
          )}
          {sagas && sagas.length > 0 && (
            <section className="row" aria-label="Filmreeksen">
              <h2>Filmreeksen <span className="row-sub">{sagas.length} reeksen in je bibliotheek</span></h2>
              <div className="saga-grid" data-focus-group>
                {sagas.map((s) => <SagaCard key={s.id} s={s} />)}
              </div>
            </section>
          )}
          {sagas && sagas.length === 0 && !building && !sagaError && <p className="placeholder-row">Nog geen filmreeksen gevonden in je bibliotheek.</p>}
        </>
      )}

      {shownLists.length > 0 && (
        <section className="row" aria-label="Lijsten">
          <h2>Lijsten <span className="row-sub">Klik voor de hele lijst · "+ Zet op Home" maakt er een widget van</span></h2>
          <div className="list-grid" data-focus-group>
            {shownLists.map((l) => <ListCard key={l.key} l={l} />)}
          </div>
        </section>
      )}
      {(filter === 'lists' || filter === 'anime') && !shownLists.length && options && (
        <p className="placeholder-row">Geen lijsten: koppel SIMKL, Trakt of MDBList bij <a {...linkProps('/settings/connections')}>Instellingen → Koppelingen</a>.</p>
      )}
      <div className="footer-space" />
    </main>
  );
}

function Mosaic({ posters, big = false }: { posters: string[]; big?: boolean }) {
  const p = [...posters, ...posters, ...posters, ...posters].slice(0, 4);
  return (
    <span className={`mosaic ${big ? 'big' : ''}`} aria-hidden="true">
      {p.length ? p.map((src, i) => <span key={i} style={{ backgroundImage: `url("${src}")` }} />) : <span className="mosaic-empty" />}
    </span>
  );
}

function AlmostCard({ s }: { s: SagaSummary }) {
  const missing = s.total - s.have;
  return (
    <a className="almost-card" {...linkProps(`/collections/${s.id}`)}>
      {s.poster ? <span className="almost-poster" style={{ backgroundImage: `url("${s.poster}")` }} aria-hidden="true" /> : <Mosaic posters={s.mosaic} />}
      <span className="almost-body">
        <strong>{s.name}</strong>
        <span className="subtle">{s.have} van {s.total} in je bibliotheek{s.seen ? ` · ${s.seen} gezien` : ''}</span>
        <span className="saga-bar"><span style={{ width: `${(s.have / s.total) * 100}%` }} /></span>
        <span className="almost-missing">Mist {missing} {missing === 1 ? 'deel' : 'delen'} → aanvragen</span>
      </span>
    </a>
  );
}

function SagaCard({ s }: { s: SagaSummary }) {
  const complete = s.have >= s.total;
  return (
    <a className="saga-card" {...linkProps(`/collections/${s.id}`)} aria-label={`${s.name}: ${s.have} van ${s.total} in je bibliotheek`}>
      <span className="saga-art">
        {s.poster ? <span className="saga-poster" style={{ backgroundImage: `url("${s.poster}")` }} aria-hidden="true" /> : <Mosaic posters={s.mosaic} big />}
        <span className={`saga-badge ${complete ? 'done' : ''}`}>{complete ? '✓ Compleet' : `${s.have}/${s.total}`}</span>
      </span>
      <span className="saga-name">{s.name}</span>
      <span className="subtle">{s.total} films · {s.seen === s.total ? 'alles gezien' : s.seen ? `${s.seen} gezien` : `${s.have} in bibliotheek`}</span>
    </a>
  );
}

// Lijstkaarten laden hun eerste titels rustig na elkaar (max. 3 tegelijk)
let running = 0;
const waiting: (() => void)[] = [];
function slot<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const go = () => {
      running++;
      fn().then(resolve, reject).finally(() => {
        running--;
        waiting.shift()?.();
      });
    };
    if (running < 3) go();
    else waiting.push(go);
  });
}

function ListCard({ l }: { l: ListDef }) {
  const { plex } = useApp();
  const [info, setInfo] = useState<{ posters: string[]; have: number; total: number } | null>(null);
  const [added, setAdded] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;
    const obs = new IntersectionObserver((e) => {
      if (done || !e[0]?.isIntersecting) return;
      done = true;
      obs.disconnect();
      const s = l.source;
      if (s.kind === 'plex' && s.list === 'collection' && s.collection) {
        void slot(() => plex.collectionItems(s.collection!, 30))
          .then((items) => setInfo({ posters: items.map((i) => plex.image(i.thumb, 200, 300)).filter((x): x is string => !!x).slice(0, 3), have: items.length, total: items.length }))
          .catch(() => setInfo({ posters: [], have: 0, total: 0 }));
      } else {
        void slot(() => layoutApi.items(s, 30, 'all'))
          .then((r) => setInfo({ posters: r.items.map((i) => i.poster).filter((x): x is string => !!x).slice(0, 3), have: r.items.filter((i) => i.ratingKey).length, total: r.items.length }))
          .catch(() => setInfo({ posters: [], have: 0, total: 0 }));
      }
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [l, plex]);

  const open = () => navigate(`/collections/list?src=${encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify({ name: l.name, source: l.source })))))}`);
  const toHome = async () => {
    setAdded('busy');
    try {
      await addWidgetToHome({ title: l.name, source: l.source, view: 'poster', show: 'all', limit: 20, enabled: true });
      setAdded('done');
    } catch {
      setAdded('error');
    }
  };

  return (
    <div className="list-card" ref={ref}>
      <button className="list-stack" onClick={open} aria-label={`${l.name} openen`}>
        {[0, 1, 2].map((i) => <span key={i} className={`ls-${i}`} style={info?.posters[i] ? { backgroundImage: `url("${info.posters[i]}")` } : undefined} />)}
      </button>
      <span className="list-body">
        <span className={`list-src tone-${l.srcTone}`}>{l.src}</span>
        <button className="list-name" onClick={open}>{l.name}</button>
        <span className="subtle">{info ? (l.plex ? `${info.total} titels` : `${info.have} van ${info.total} in bibliotheek`) : 'Laden…'}</span>
        <button className="btn btn-sm" disabled={added === 'busy' || added === 'done'} onClick={() => void toHome()}>
          {added === 'done' ? '✓ Op Home' : added === 'error' ? 'Mislukt, opnieuw' : '+ Zet op Home'}
        </button>
      </span>
    </div>
  );
}
